import React, { useEffect, useState } from 'react';
import { HelpCircle, AlertCircle, Loader, Eye, EyeOff } from 'react-feather';

import { InputFieldProps } from './InputField.types';
import { getInputFieldStyles } from './styleUtils';
import { theme } from './theme';
import './InputField.css';
import { Typography } from '../Typography/Typography';
import { ERROR_COLORS } from '../globalTheme';

const InputField: React.FC<InputFieldProps> = ({
	label,
	placeholder,
	hint,
	tooltipText,
	startAction,
	endAction,
	loading,
	error,
	disabled,
	required,
	type,
	value,
	onChange,
	onBlur,
	name,
	id,
	size,
	charLenghtError,
	maxChars = 20000,
	shape,
	focused = false,
	dataTestId,
	autofocus,
	hasCharLengthError,
	strictMaxChars = false
}) => {
	const [isFocused, setIsFocused] = useState(focused);
	const [charCount, setCharCount] = useState(0);
	const dynamicStyles = getInputFieldStyles(theme, size, shape);
	const [showPassword, setShowPassword] = useState(false);

	useEffect(() => {
		if (charCount > maxChars) {
			if (hasCharLengthError) hasCharLengthError(true);
		} else {
			if (hasCharLengthError) hasCharLengthError(false);
		}
	}, [charCount]);

	const componentStyles = () => {
		const baseStyles = dynamicStyles.input_css;
		const focusedStyles = isFocused && !error ? dynamicStyles.inputFocused_css : {};
		const disabledStyles = disabled || loading ? dynamicStyles.inputDisabled_css : {};
		const errorStyles = error ? dynamicStyles.inputError_css : {};
		const errorFocusedStyles = isFocused && error ? dynamicStyles.errorFocused_css : {};

		return {
			...baseStyles,
			...focusedStyles,
			...disabledStyles,
			...errorStyles,
			...errorFocusedStyles
		};
	};

	const renderIcon = (
		loading: boolean | undefined,
		error: boolean | undefined,
		tooltipText: string | undefined
	) => {
		if (error) {
			return <AlertCircle size={16} color={dynamicStyles.icons.error} data-testid="error-icon" />;
		}

		if (loading && !error) {
			return (
				<Loader
					size={16}
					color={dynamicStyles.icons.loading}
					className="spin-animation"
					data-testid="loading-icon"
				/>
			);
		}

		if (tooltipText && !error) {
			return (
				<HelpCircle size={16} color={dynamicStyles.icons.tooltip} data-testid="tooltip-icon" />
			);
		}

		return null;
	};
	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (
			event.key === 'Enter' &&
			React.isValidElement(endAction) &&
			endAction.props &&
			endAction.props.onClick &&
			charCount <= maxChars &&
			!error &&
			charCount > 0
		) {
			endAction.props.onClick();
		}
	};

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	const renderPasswordToggle = () => {
		if (type === 'password') {
			return (
				<button
					type="button"
					onClick={togglePasswordVisibility}
					className="password-toggle mx-1"
					aria-label={showPassword ? 'Hide password' : 'Show password'}
				>
					{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
				</button>
			);
		}
		return null;
	};

	return (
		<div className="input-container">
			<label
				htmlFor={id}
				className="input-label"
				id="input-label"
				style={dynamicStyles.inputLabel_css}
			>
				{label}
			</label>
			<div style={componentStyles()} className={'input'}>
				<span className="start-action">{startAction}</span>
				<input
					autoFocus={autofocus}
					data-testid={dataTestId}
					aria-label={label || 'input'}
					aria-describedby="input-label"
					aria-invalid={error ? 'true' : 'false'}
					aria-required={required ? 'true' : 'false'}
					onFocus={() => setIsFocused(true)}
					onBlur={(e) => {
						setIsFocused(false);
						if (onBlur) {
							onBlur(e);
						}
					}}
					onKeyDown={handleKeyDown}
					type={type === 'password' && showPassword ? 'text' : type}
					placeholder={placeholder}
					value={value}
					onChange={(e) => {
						if (typeof onChange === 'function') {
							onChange(e);
						}
						setCharCount(e.target.value.length);
					}}
					name={name}
					id={id}
					maxLength={strictMaxChars ? maxChars : undefined}
					disabled={disabled || loading}
					required={required}
					className="input-field"
				/>
				{renderPasswordToggle()}
				{renderIcon(loading, error, tooltipText)}
				{endAction}
			</div>
			<span
				style={{
					...dynamicStyles.inputHint_css,
					...(error ? dynamicStyles.inputHintError_css : {})
				}}
			>
				{hint}
			</span>
			{charCount > maxChars && (
				<div>
					<Typography color={ERROR_COLORS.error500} size="sm">
						{charLenghtError}
					</Typography>
				</div>
			)}
		</div>
	);
};

export default InputField;
